/**
 * Calendar endpoint helpers — typed wire shape for the Track C calendar hub.
 *
 * Ports the v1 ``ando-frontend/app/api/calendar.ts`` listing call. Only the
 * shape the calendar hub needs lands here; OAuth flows (``connect`` /
 * ``disconnect``) stay BE-driven via redirect — there's no SPA fetcher.
 *
 * BE source of truth:
 *   ``ando-agent/src/api/v1/endpoints/calendar.py::list_calendar_connections``
 *   wire shape from ``CalendarTokenService.list_user_connections``:
 *
 *   {
 *     connections: [
 *       {
 *         id, calendar_provider, provider, email_address,
 *         expires_at, is_expired,
 *         created_at, updated_at, last_used_at
 *       }
 *     ]
 *   }
 *
 *   ``provider`` and ``calendar_provider`` are duplicates of the same
 *   underlying value ("google_calendar" | "outlook_calendar"); the BE keeps
 *   both for backward-compat with older FE consumers.
 */

import { apiClient } from "../ApiClient.js";

/** Provider tag — matches ``CalendarProvider`` enum BE-side. */
export type CalendarProviderId = "google_calendar" | "outlook_calendar";

export interface CalendarConnection {
  /** UUID from ``user_connections``. */
  readonly id: string;
  /** BE duplicates the provider on both keys; consumers may read either. */
  readonly calendar_provider: CalendarProviderId | string;
  readonly provider: CalendarProviderId | string;
  readonly email_address: string;
  /** ISO 8601; null when the BE couldn't parse the token's expiry timestamp. */
  readonly expires_at: string | null;
  /**
   * ``true`` only when ``token_status === "needs_reauth"``. Set by connectors
   * on permanent OAuth errors (``invalid_grant``, revoked). A successful
   * refresh / reconnect resets the flag to ``false``.
   */
  readonly is_expired: boolean;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly last_used_at: string | null;
}

export interface CalendarConnectionsResponse {
  readonly connections: readonly CalendarConnection[];
}

/** GET ``/calendar/connections`` — list every calendar token for the user. */
export async function getCalendarConnections(): Promise<CalendarConnectionsResponse> {
  return await apiClient.get<CalendarConnectionsResponse>("/calendar/connections");
}

/**
 * Single calendar event — wire shape mirrors
 * ``ando-frontend/app/pages/CalendarHubPage.tsx::CalendarEvent`` (lines 87-103).
 *
 * BE source: ``ando-agent/src/api/v1/endpoints/calendar_management.py::events``
 * returning ``{ events: CalendarEvent[] }``.
 */
export interface CalendarEvent {
  readonly event_id: string;
  readonly title: string;
  /** ISO 8601 timestamp (with timezone). */
  readonly start_time: string;
  readonly end_time: string;
  readonly location?: string;
  readonly description?: string;
  readonly is_all_day: boolean;
  readonly calendar_id: string;
  readonly timezone?: string;
  readonly organizer?: string;
  readonly attendees?: readonly string[];
  readonly is_recurring?: boolean;
  readonly conference_url?: string;
  readonly web_link?: string;
  readonly provider?: string;
}

export interface CalendarEventsResponse {
  readonly events: readonly CalendarEvent[];
}

export interface GetCalendarEventsArgs {
  /** ``user_id`` query param — BE still requires the explicit user id. */
  readonly userId: string;
  readonly provider: CalendarProviderId | string;
  readonly emailAddress: string;
  /** Optional calendar id; omit to read the primary calendar. */
  readonly calendarId?: string;
  /** ISO 8601 range bounds. Default range is BE-controlled when omitted. */
  readonly start?: string;
  readonly end?: string;
}

/**
 * GET ``/calendar/events`` — list events for a single provider+account.
 *
 * v1 reference: ``CalendarHubPage.tsx`` lines 175-205 (loop the connections
 * map, call once per ``(provider, email)`` pair).
 */
export async function getCalendarEvents(
  args: GetCalendarEventsArgs,
): Promise<CalendarEventsResponse> {
  const params = new URLSearchParams({
    user_id: args.userId,
    provider: args.provider,
  });
  // BE accepts the email param even when calendarId is omitted; mirror v1.
  if (args.emailAddress) params.append("email_address", args.emailAddress);
  if (args.calendarId) params.append("calendar_id", args.calendarId);
  if (args.start) params.append("start", args.start);
  if (args.end) params.append("end", args.end);
  return await apiClient.get<CalendarEventsResponse>(
    `/calendar/events?${params.toString()}`,
  );
}
